import Button from '@atlaskit/button/new';
import Spinner from '@atlaskit/spinner';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { useDatabaseContent } from '@databases/database-state';
import { messages } from '@databases/intl/messages';
import { logError } from '@databases/logs';
import { appStoreUtils } from '@databases/redux/store';
import { getBridgeAPI } from '@databases/utils/database/bridge';
import { loadDatabaseByReferenceId } from '@databases/utils/redux/databases/actions';
import { selectDatabaseByContentIdStatus } from '@databases/utils/redux/databases/selectors';
import { AsyncOperationStatus } from '@databases/utils/redux/redux';
import {
  deserializeError,
  isStandalone as isStandaloneCheck,
} from '@databases/utils/utils';
import React, { FunctionComponent, lazy, Suspense, useEffect } from 'react';
import { useIntl } from 'react-intl-next';

import { dynamicImport } from '@canvas/dynamic-import';

import genericErrorIllustration from '../../../../assets/generic_error.svg';
import { DatabaseActionKey } from '../../../../common/components/database/DatabaseActions';
import DatabaseContent from '../../../../common/components/database-content/DatabaseContent';
import DatabaseContentSkeleton from '../../../../common/components/database-content/DatabaseContentSkeleton';

const isStandalone = isStandaloneCheck();

const ShrinkingEmptyState = lazy(() =>
  dynamicImport(() => import('../../../../common/components/ShrinkingEmptyState'))
);

const actions: DatabaseActionKey[] = ['import', 'print', 'help', 'copy', 'feedback'];
if (isStandalone) {
  actions.push('lock');
}

const DatabaseDetails: FunctionComponent = () => {
  const { contentId, referenceId, spaceId, spaceKey, spaceName, title } =
    useDatabaseContent();
  const dispatch = appStoreUtils.useDispatch();

  const intl = useIntl();
  const { formatMessage } = intl;

  useEffect(() => {
    const loadDatabase = async () => {
      if (!contentId || !referenceId || !spaceId || !spaceKey) {
        return;
      }

      GlobalPageLoadExperience.mark('database-load-start');
      await dispatch(
        loadDatabaseByReferenceId(
          {
            contentId,
            referenceId,
            spaceId: Number(spaceId),
            spaceKey,
            intl,
            spaceName: spaceName ?? '',
            title: title ?? '',
          },
          { throttle: false }
        )
      );
    };

    loadDatabase();
  }, [contentId, dispatch, referenceId, spaceId, spaceKey, spaceName, title, intl]);

  const databaseStatus = appStoreUtils.useSelector((state) =>
    contentId ? selectDatabaseByContentIdStatus(state, { contentId }) : undefined
  );

  useEffect(() => {
    const markBridgeLoadSucceeded = async () => {
      GlobalPageLoadExperience.mark('database-load-end');
      GlobalPageLoadExperience.success({ force: true });
      (await getBridgeAPI()).onContentLoadSucceeded?.({});
    };

    if (databaseStatus?.status === AsyncOperationStatus.SUCCEEDED) {
      markBridgeLoadSucceeded();
    }
  }, [databaseStatus]);

  switch (databaseStatus?.status) {
    case AsyncOperationStatus.ERROR: {
      const error = deserializeError(databaseStatus.error);
      const formattedError = {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
      GlobalPageLoadExperience.failure({
        metadata: { error: formattedError },
        force: true,
      });

      logError(new Error(`Couldn't load link with error: ${error}`));
      return (
        <Suspense fallback={<Spinner interactionName="load" />}>
          <ShrinkingEmptyState
            header={formatMessage(messages['embed-view.error-heading-text'])}
            description={formatMessage(messages['embed-view.error-description-text'])}
            imageUrl={genericErrorIllustration}
            primaryAction={
              <Button appearance="primary" onClick={() => window.location.reload()}>
                {formatMessage(messages['embed-view.try-again-button-text'])}
              </Button>
            }
            maxImageWidth={300}
            maxImageHeight={250}
          />
        </Suspense>
      );
    }
    case AsyncOperationStatus.SUCCEEDED:
      if (!contentId) {
        throw new Error('Content ID is missing.');
      }

      return (
        <DatabaseContent
          contentId={contentId}
          isContentEditable
          isFieldsEditable
          actions={actions}
        />
      );
    default:
      return <DatabaseContentSkeleton />;
  }
};

export default DatabaseDetails;
